import { CONSULTATION_TYPE, SurveyChoicesComponent } from '@abiglobalhealth/go-types'
import { SurveyChoice } from '@components/SurveyChoices/SurveyChoice'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormErrors } from '@/hooks/useFormErrors'
import { useIndications } from '@/hooks/useIndications'
import { useIsConsultationType } from '@/hooks/useIsConsultationType'
import { useModule } from '@/hooks/useModule'
import { useStep } from '@/hooks/useStep'
import i18n from '@/locale/i18n'

export type SurveyChoicesProps = Omit<SurveyChoicesComponent, 'componentType'> & {
  tabIndex: number
}

export const SurveyChoices = ({ id, textKey, options, descriptionTextKey, tabIndex }: SurveyChoicesProps) => {
  const { t } = useTranslation()

  const { currentModule } = useModule()
  const { currentStep } = useStep()
  const { indications, addIndication } = useIndications()
  const { getFieldError } = useFormErrors()
  const isPrescription = useIsConsultationType(CONSULTATION_TYPE.PRESCRIPTION)

  const initialSelected = useMemo(() => indications.find((indication) => indication.id === currentStep!.id)?.answers || [], [])

  const [selected, setSelected] = useState<string | null>(null)

  useEffect(() => {
    if (!selected) return

    if (!currentModule?.id) throw new Error('Module id is undefined')

    const selectedOption = options.find((option) => option.id === selected)
    if (!selected || !selectedOption) throw new Error('No selected option found')

    addIndication({
      id: currentStep!.id,
      questionKey: textKey,
      question: `${t(textKey)}${i18n.exists(descriptionTextKey) ? `\n${t(descriptionTextKey)}` : ''}`,
      answers: [
        {
          id: selectedOption.id,
          key: selectedOption.textKey,
          value: t(selectedOption.textKey)
        }
      ]
    })
  }, [selected])

  const error = getFieldError(id)
  const hasDescription = i18n.exists(descriptionTextKey)

  return (
    <div id={id} data-testid={`${id}-container`} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        {isPrescription ? (
          <label dangerouslySetInnerHTML={{ __html: t(textKey)! }} />
        ) : (
          <>
            {hasDescription && <span className="text-center whitespace-pre-wrap">{t(descriptionTextKey)}</span>}
            <label className={'text-center text-2xl font-bold'} dangerouslySetInnerHTML={{ __html: t(textKey)! }} />
          </>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-4">
          {options.map((option, index) => {
            const elementTabIndex = Math.abs(tabIndex - options.length + index) + 1
            return (
              <SurveyChoice
                key={option.id}
                selected={selected}
                setSelected={setSelected}
                tabIndex={elementTabIndex}
                initialSelected={initialSelected}
                groupError={error}
                {...option}
              />
            )
          })}
        </div>

        <span className="text-error min-h-[1.5rem] text-sm" data-testid={`${id}-error`}>
          {error && t(error)}
        </span>
      </div>
      {isPrescription && hasDescription && <span className="text-sm whitespace-pre-wrap">{t(descriptionTextKey)}</span>}
    </div>
  )
}
